import { useEffect, useState } from 'preact/hooks'
import type { Persona } from '../types'
import { PERSONA_INFO } from '../types'
import { SearchDialog } from './SearchDialog'
import { getQuotaStatus } from '../services/api'
import { enforceBackendAccess } from '../services/backendConfig'
import type { QuickChatEntry } from '../services/quickChat'

interface LandingPageProps {
  personas: Persona[]
  /** Currently active persona; its card gets a highlight ring */
  selectedPersonaId: string
  onSelectPersona: (persona: Persona) => void
  onNavigate: (view: string, focusId?: string) => void
  userEmail: string
  onSignOut: () => void
  personaImages: Record<string, string>
  /** Switch to the state-first home screen prototype */
  onTryNewHome?: () => void
  /** Continue a quick-chat answer from the search dialog in full chat */
  onContinueQuickChat?: (entry: QuickChatEntry) => void
  /** Open a chat with the selected persona, optionally sending text */
  onStartChat?: (text?: string) => void
}

/** Feature tile definition */
interface FeatureTile {
  id: string
  label: string
  image: string      // filename in /buttons/
  fallback: string   // emoji fallback
  action?: string    // opens cloud data panel
  requiresPaid?: boolean // dim & disable for free-tier users
}

/** Main tiles — 2 rows of 6, using toon button images.
 *  `action` is the view name to navigate to when clicked. */
const MAIN_TILES: FeatureTile[] = [
  { id: 'user',      label: 'User',       image: 'toon_sea_smol_user.png',      fallback: '\u{1F464}', action: 'user' },
  { id: 'email',     label: 'Email',      image: 'toon_sea_smol_email.png',     fallback: '\u{1F4E7}', action: 'email', requiresPaid: true },
  { id: 'tasks',     label: 'Tasks',      image: 'toon_sea_smol_tasks.png',     fallback: '\u{2705}',  action: 'tasks' },
  { id: 'files',     label: 'Files',      image: 'toon_sea_smol_files.png',     fallback: '\u{1F4C1}', action: 'files', requiresPaid: true },
  { id: 'calendar',  label: 'Calendar',   image: 'toon_sea_smol_calendar.png',  fallback: '\u{1F4C5}', action: 'calendar' },
  { id: 'notes',     label: 'Notes',      image: 'toon_sea_smol_notes.png',     fallback: '\u{1F4DD}', action: 'notes' },
  { id: 'settings',  label: 'Settings',   image: 'toon_sea_smol_settings.png',  fallback: '\u{2699}\u{FE0F}', action: 'settings' },
  { id: 'projects',  label: 'Projects',   image: 'toon_sea_smol_projects.png',  fallback: '\u{1F4CB}', action: 'projects' },
  { id: 'box',       label: 'BOX',        image: 'toon_sea_smol_box.png',       fallback: '\u{1F4E6}', action: 'box' },
  { id: 'apps',      label: 'APPS',       image: 'toon_sea_smol_apps.png',      fallback: '\u{1F9E9}', action: 'apps', requiresPaid: true },
  { id: 'followups', label: 'Calls',      image: 'toon_sea_smol_followups.png', fallback: '\u{1F4DE}', action: 'calls' },
  { id: 'alerts',    label: 'Alerts',     image: 'toon_sea_smol_alerts.png',    fallback: '\u{1F6A8}', action: 'alerts' },
]


export function LandingPage({ personas, selectedPersonaId, onSelectPersona, onNavigate, userEmail, onSignOut, personaImages, onTryNewHome, onContinueQuickChat, onStartChat }: LandingPageProps) {
  const [showSearch, setShowSearch] = useState(false)
  // Resolve subscription tier so we can gate paid-only feature tiles
  // (email, files, apps). Default to undefined while loading so we don't
  // briefly render the tiles as enabled and then disable them.
  const [subscriptionTier, setSubscriptionTier] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!userEmail) return
    let cancelled = false
    getQuotaStatus(userEmail).then((s) => {
      if (cancelled) return
      const tier = (s?.subscription_tier || 'free').toLowerCase()
      setSubscriptionTier(tier)
      // Force non-owners off the dev backend (reloads the page if a switch is needed).
      enforceBackendAccess(tier)
    }).catch(() => {
      if (!cancelled) {
        setSubscriptionTier('free')
        enforceBackendAccess('free')
      }
    })
    return () => { cancelled = true }
  }, [userEmail])
  const isFreeTier = subscriptionTier === 'free'

  return (
    <div style={styles.container}>
      {/* Top bar */}
      <div style={styles.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onTryNewHome && (
            <button onClick={onTryNewHome} style={styles.signOutBtn}>Try new home</button>
          )}
        </div>
        <div style={styles.userInfo}>
          <span style={styles.userEmail}>{userEmail}</span>
          <button onClick={onSignOut} style={styles.signOutBtn}>Sign out</button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Persona row */}
        <div class="landing-personas" style={styles.personaRow}>
          {personas.map((persona) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              imageUrl={personaImages[persona.id]}
              selected={persona.id === selectedPersonaId}
              onSelect={onSelectPersona}
            />
          ))}
        </div>

        {/* Search bar — opens the search dialog when clicked */}
        <div style={styles.searchContainer}>
          <button
            class="landing-searchbar"
            onClick={() => setShowSearch(true)}
            style={{ ...styles.searchBar, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit' }}
          >
            <span style={{ opacity: 0.5, marginRight: 8 }}>{'\u{1F50D}'}</span>
            <span style={{ opacity: 0.4, fontSize: 16 }}>Search conversations, tasks, notes — or ask a quick question...</span>
          </button>
        </div>

        {/* Main feature tiles — 4-column grid with toon images */}
        <div class="landing-grid" style={styles.mainGrid}>
          {MAIN_TILES.map((tile) => {
            const locked = isFreeTier && !!tile.requiresPaid
            return (
              <button
                key={tile.id}
                style={{
                  ...styles.mainTile,
                  ...(locked ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                disabled={locked}
                title={locked ? 'Available on paid plans' : undefined}
                onClick={() => { if (!locked && tile.action) onNavigate(tile.action) }}
              >
                <img
                  src={`/buttons/${tile.image}`}
                  alt={tile.label}
                  style={{ width: 80, height: 80, objectFit: 'contain' }}
                  onError={(e) => {
                    const el = e.target as HTMLImageElement
                    el.style.display = 'none'
                    el.parentElement!.querySelector('.tile-fallback')?.removeAttribute('hidden')
                  }}
                />
                <span class="tile-fallback" hidden style={{ fontSize: 32 }}>{tile.fallback}</span>
                <span style={styles.mainTileLabel}>{tile.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {showSearch && (
        <SearchDialog
          onClose={() => setShowSearch(false)}
          onNavigate={onNavigate}
          personaId={selectedPersonaId}
          onContinueQuickChat={onContinueQuickChat}
          onStartChat={onStartChat}
          personaName={personas.find((p) => p.id === selectedPersonaId)?.name}
        />
      )}
    </div>
  )
}

/** Individual persona card with image fallback to emoji. The "local
 *  only" badge that used to overlay personas without a cloud_user_memory
 *  row was dropped: when the user has no cloud profile picture for a
 *  persona we already render the stock emoji avatar, which is enough
 *  signal on its own that the persona hasn't been customized yet. */
function PersonaCard({ persona, imageUrl, selected, onSelect }: { persona: Persona; imageUrl?: string; selected: boolean; onSelect: (p: Persona) => void }) {
  const [imgFailed, setImgFailed] = useState(false)
  const info = PERSONA_INFO[persona.id]
  const showImage = imageUrl && !imgFailed

  return (
    <button
      onClick={() => onSelect(persona)}
      style={styles.personaCard}
      title={selected ? `Chat with ${persona.name}` : `Select ${persona.name}`}
      aria-pressed={selected}
    >
      <div class="persona-avatar" style={{
        ...styles.personaAvatar,
        ...(showImage ? { background: 'transparent', border: 'none' } : {}),
        // Ring drawn with box-shadow so it also shows when the image hides the border
        ...(selected ? { boxShadow: '0 0 0 3px var(--primary)' } : {}),
      }}>
        {showImage ? (
          <img
            src={imageUrl}
            alt={persona.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 22 }}
            onError={() => setImgFailed(true)}
          />
        ) : imgFailed ? (
          <span style={{ fontSize: 56 }}>{info?.emoji || persona.emoji}</span>
        ) : (
          <div style={{ width: '100%', height: '100%', borderRadius: 22, background: 'rgba(45, 165, 160, 0.15)' }} />
        )}
      </div>
      <div style={{ ...styles.personaName, ...(selected ? { color: 'var(--text)', fontWeight: 600 } : {}) }}>{persona.name}</div>
    </button>
  )
}

const styles: Record<string, Record<string, string | number>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'var(--bg)',
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 40px',
    flexShrink: 0,
    zIndex: 1,
    maxWidth: 800,
    margin: '0 auto',
    width: '100%',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  userEmail: {
    fontSize: 14,
    color: 'var(--text-secondary)',
  },
  signOutBtn: {
    padding: '6px 14px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: 13,
  },
  personaRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 22,
    padding: '32px 32px 24px',
    maxWidth: 1220,
    margin: '0 auto',
    width: '100%',
    flexShrink: 0,
  },
  personaCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: 16,
    transition: 'background 0.15s',
    color: 'var(--text)',
    minWidth: 0,
  },
  personaAvatar: {
    width: '88%',
    aspectRatio: '1',
    borderRadius: 22,
    background: 'var(--assistant-bubble)',
    border: '2px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  personaName: {
    fontSize: 16,
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '4px 32px 20px',
    flexShrink: 0,
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: 640,
    padding: '14px 24px',
    background: 'var(--user-bubble)',
    border: '1px solid var(--border)',
    borderRadius: 24,
    color: 'var(--text)',
    fontSize: 16,
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 22,
    padding: '0 32px 32px',
    maxWidth: 1220,
    margin: '0 auto',
    width: '100%',
  },
  mainTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    aspectRatio: '1',
    padding: '16px',
    background: 'var(--system-bubble)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    cursor: 'pointer',
    color: 'var(--text)',
    transition: 'background 0.15s',
  },
  mainTileLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  toonGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    padding: '0 40px 40px',
    maxWidth: 700,
    margin: '0 auto',
    width: '100%',
  },
  toonTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: 80,
    height: 80,
    padding: '8px',
    background: 'var(--system-bubble)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    cursor: 'pointer',
    color: 'var(--text)',
    transition: 'background 0.15s',
  },
  toonTileLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--text-muted)',
  },
}
